import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Award, Target, TrendingUp } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/Card';
import { CommunicationBadgeChips } from '../../../components/student/CommunicationBadgeChips';
import { getCommunicationBadgesMe } from '../../../services/api/communicationLearning';
import { useFeatureAccess } from '../../../contexts/FeatureAccessContext';
import { useStudentMe } from '../../roster/hooks/useStudentMe';
import { useTutorTenantLabels } from '../../tutor/hooks/useTutorTenantLabels';
import { useVocabularyJourneyData } from '../hooks/useVocabularyJourneyData';
import { computeVocabularyMilestones, type CommBadgeItem } from '../../../utils/gamification';
import { ROUTES } from '../../../utils/constants';
import { DashboardCardSkeleton, DashboardEmptyState } from './dashboardUi';

export function AchievementsCard() {
  const { t } = useTranslation();
  const { isSchoolFeatureEnabled } = useFeatureAccess();
  const { labels } = useTutorTenantLabels();
  const { isRosterLoading } = useStudentMe();
  const vocab = useVocabularyJourneyData();
  const commEnabled = isSchoolFeatureEnabled('communication_tutor') && !labels.isMalay;
  const vocabEnabled = isSchoolFeatureEnabled('vocabulary');

  const [badges, setBadges] = useState<CommBadgeItem[]>([]);
  const [badgesLoading, setBadgesLoading] = useState(commEnabled);
  const [badgesError, setBadgesError] = useState(false);

  const loadBadges = useCallback(() => {
    if (!commEnabled || isRosterLoading) return;
    setBadgesLoading(true);
    setBadgesError(false);
    getCommunicationBadgesMe()
      .then((b) => setBadges((b.items as CommBadgeItem[]) ?? []))
      .catch(() => {
        setBadges([]);
        setBadgesError(true);
      })
      .finally(() => setBadgesLoading(false));
  }, [commEnabled, isRosterLoading]);

  useEffect(() => {
    if (!commEnabled) {
      setBadges([]);
      setBadgesLoading(false);
      return;
    }
    loadBadges();
  }, [commEnabled, loadBadges]);

  const milestones = useMemo(
    () => computeVocabularyMilestones(vocab.journey.words_mastered, vocab.journey.practice_streak),
    [vocab.journey.words_mastered, vocab.journey.practice_streak],
  );
  const earnedMilestones = milestones.filter((m) => m.earned);

  const loading = badgesLoading || (vocabEnabled && vocab.loading);
  const hasBadges = badges.some((b) => b.badge_key);
  const hasMilestones = earnedMilestones.length > 0;

  if (!commEnabled && !vocabEnabled) return null;

  const emptyMessage = labels.isMalay
    ? t('dashboard.studentHome.achievementsEmptyMalay')
    : t('dashboard.studentHome.achievementsEmpty');

  if (loading) {
    return <DashboardCardSkeleton className="h-full" />;
  }

  if (!hasBadges && !hasMilestones) {
    return (
      <Card className="border-violet-200/80 bg-gradient-to-r from-violet-50/80 to-purple-50/50 dark:from-violet-950/30 dark:to-purple-950/20 dark:border-violet-800/50 h-full">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2 text-purple-900 dark:text-purple-100">
            <Award className="h-5 w-5" aria-hidden />
            {t('dashboard.studentHome.achievements')}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <DashboardEmptyState>
            {badgesError
              ? t('dashboard.studentHome.achievementsBadgesError')
              : emptyMessage}
          </DashboardEmptyState>
          {badgesError ? (
            <button
              type="button"
              onClick={loadBadges}
              className="text-sm font-semibold text-purple-600 dark:text-purple-300"
            >
              {t('dashboard.studentHome.retryBadges')}
            </button>
          ) : null}
          <Link
            to={ROUTES.STUDENT_PROGRESS}
            className="inline-flex text-sm font-semibold text-purple-600 hover:text-purple-800 dark:text-purple-300"
          >
            {t('dashboard.studentHome.viewAllArrow')}
          </Link>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-violet-200/80 bg-gradient-to-r from-violet-50/80 to-purple-50/50 dark:from-violet-950/30 dark:to-purple-950/20 dark:border-violet-800/50 h-full">
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className="text-base flex items-center gap-2 text-purple-900 dark:text-purple-100">
          <Award className="h-5 w-5" aria-hidden />
          {t('dashboard.studentHome.achievements')}
        </CardTitle>
        <Link
          to={ROUTES.STUDENT_PROGRESS}
          className="text-xs font-semibold text-purple-700 hover:text-purple-900 dark:text-purple-300 dark:hover:text-purple-100"
        >
          {t('dashboard.studentHome.viewAll')}
        </Link>
      </CardHeader>
      <CardContent className="space-y-4">
        {hasBadges ? (
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-2">
              {t('dashboard.studentHome.latestBadges')}
            </p>
            <CommunicationBadgeChips badges={badges.slice(0, 4)} />
          </div>
        ) : null}
        {hasMilestones ? (
          <ul className="space-y-2">
            {earnedMilestones.slice(0, 2).map((m) => (
              <li
                key={m.id}
                className="flex items-start gap-2 rounded-xl border border-emerald-200/80 dark:border-emerald-800/50 bg-white/70 dark:bg-emerald-950/20 px-3 py-2"
              >
                <Target className="h-4 w-4 shrink-0 text-emerald-600 dark:text-emerald-400 mt-0.5" aria-hidden />
                <div className="min-w-0">
                  <p
                    className={`text-sm font-semibold text-slate-900 dark:text-white${labels.isMalay ? '' : ' tamil-text'}`}
                    lang={labels.isMalay ? undefined : 'ta'}
                  >
                    {t(m.titleKey)}
                  </p>
                </div>
              </li>
            ))}
          </ul>
        ) : null}
        {vocab.journey.practice_streak >= 3 ? (
          <p className="text-xs text-emerald-700 dark:text-emerald-300 flex items-center gap-1">
            <TrendingUp className="h-3.5 w-3.5" aria-hidden />
            {t('dashboard.studentHome.dayPracticeStreak', { count: vocab.journey.practice_streak })}
          </p>
        ) : null}
      </CardContent>
    </Card>
  );
}
